/**
 * @fileoverview ArtistBioAttribution — PIP-146 CC BY-SA credit footer.
 *
 * Renders the mandatory source credit + link for an enriched artist bio:
 * "Source: Wikipedia · License: CC BY-SA 4.0", with the source linking to the
 * article and the license linking to the license deed (`rel="license"`).
 *
 * This is the single shared enforcement point for the license policy — every
 * surface that renders an enriched `Artist.description` (LineupSection,
 * ArtistHero, …) MUST mount this next to the text. Renders nothing when
 * `attribution` is absent (manual/unenriched descriptions need no credit).
 */

import React from 'react';
import { cn } from '@/components/atoms/primitives/utils';
import type { DescriptionAttribution } from '@/types/eventDetail';

/**
 * License label → deed URL. Only licenses we actually store are mapped; an
 * unknown label renders as plain text rather than guessing a URL.
 */
const LICENSE_DEED_URLS: Record<string, string> = {
  'CC BY-SA 4.0': 'https://creativecommons.org/licenses/by-sa/4.0/',
};

export interface ArtistBioAttributionProps {
  attribution: DescriptionAttribution | null | undefined;
  className?: string;
}

export function ArtistBioAttribution({ attribution, className }: ArtistBioAttributionProps) {
  if (!attribution) {
    return null;
  }

  const licenseUrl = LICENSE_DEED_URLS[attribution.license];
  const linkClassName = 'underline underline-offset-2 hover:text-muted-foreground';

  return (
    // FRE-1058 — full `text-muted-foreground` (was `/70`) so the credit text +
    // links clear WCAG 2.2 AA contrast (the 70% blend measured 4.26:1 on the
    // dark artist surface, just under the 4.5:1 floor).
    <p
      className={cn('mt-1 text-xs text-muted-foreground', className)}
      data-testid="artist-bio-attribution"
    >
      Source:{' '}
      <a
        href={attribution.sourceUrl}
        target="_blank"
        rel="noopener noreferrer"
        className={linkClassName}
      >
        {attribution.source}
      </a>{' '}
      · License:{' '}
      {licenseUrl ? (
        <a
          href={licenseUrl}
          target="_blank"
          rel="license noopener noreferrer"
          className={linkClassName}
        >
          {attribution.license}
        </a>
      ) : (
        attribution.license
      )}
    </p>
  );
}
